import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Fetchexecutequery,
  Fetchupdatequery,
} from "../../../services/process/BRS1/brsService7";

import { headers, baseUrl } from "../../../services/common/credentials";
import { CreateData } from "../../../services/process/process_data/process_data";
import {
  Search, Filter, Grid, List, MapPin, Calendar, Building,
  Star, ArrowRight, ArrowLeft, Check, CheckCircle, Bell,
  Clock, Eye, Send, Flag, Bookmark, Mail, Phone,
  Globe, AlertCircle, Minus, Plus, Paperclip, Save, X,
  ChevronLeft, ChevronRight, FileText, Zap, Download,
  ClipboardList, RefreshCw, Users, MessageCircle, Image as ImageIcon,
  CalendarClock, Share2,
} from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────────────────────
const getUserCredentials = () =>
  JSON.parse(sessionStorage.getItem("userCredentials") || "{}");

const getFormattedDate = () =>
  new Date().toISOString().slice(0, 19).replace("T", " ");

const FONT = "";

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  navy:        "#0a2352",
  navyDark:    "#061535",
  navyMid:     "#143272",
  orange:      "#f97316",
  orangeHover: "#ea6c0a",
  bg:          "#eef3f9",
  card:        "#ffffff",
  border:      "#d4dce8",
  text:        "#000000",
  muted:       "#000000",
  light:       "#e8eef5",
  success:     "#16a34a",
  danger:      "#dc2626",
  warn:        "#d97706",
  purple:      "#7c3aed",
  accent:      "#2563eb",
};

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_META = {
  // Event statuses
  draft:                { label: "Draft",                bg: "#f3f4f6", color: "#6b7280" },
  published:            { label: "Published",            bg: "#dbeafe", color: "#1d4ed8" },
  registration_open:    { label: "Reg. Open",            bg: "#d1fae5", color: "#065f46" },
  registration_closed:  { label: "Reg. Closed",          bg: "#fef3c7", color: "#92400e" },
  under_review:         { label: "Under Review",         bg: "#fef3c7", color: "#92400e" },
  invitations_released: { label: "Invitations Released", bg: "#ede9fe", color: "#5b21b6" },
  completed:            { label: "Completed",            bg: "#d1fae5", color: "#065f46" },
  archived:             { label: "Archived",             bg: "#f3f4f6", color: "#6b7280" },
  // Application statuses
  applied:              { label: "Applied",              bg: "#dbeafe", color: "#1d4ed8" },
  shortlisted:          { label: "Shortlisted",          bg: "#d1fae5", color: "#065f46" },
  rejected:             { label: "Rejected",             bg: "#fee2e2", color: "#991b1b" },
  invited:              { label: "Invited",              bg: "#ede9fe", color: "#5b21b6" },
  accepted:             { label: "Accepted",             bg: "#d1fae5", color: "#065f46" },
  declined:             { label: "Declined",             bg: "#fef3c7", color: "#92400e" },
  expired:              { label: "Expired",              bg: "#f3f4f6", color: "#6b7280" },
  pending_review:       { label: "Pending Review",       bg: "#fef3c7", color: "#92400e" },
};

const StatusBadge = ({ status, label }) => {
  const m = STATUS_META[status] || { label: label || (status || "").replace(/_/g," "), bg: "#f3f4f6", color: "#6b7280" };
  return (
    <span style={{
      background: m.bg, color: m.color,
      padding: "4px 11px", borderRadius: 99,
      fontSize: 13, fontWeight: 700, letterSpacing: .4,
      textTransform: "uppercase", whiteSpace: "nowrap",
    }}>
      {m.label}
    </span>
  );
};

// ── Banner palette ────────────────────────────────────────────────────────────
const BANNER_COLORS = ["#0a2352","#143272","#1e40af","#7c3aed","#0891b2","#065f46","#92400e"];
const getBannerColor = (id) => BANNER_COLORS[(id || 0) % BANNER_COLORS.length];

// ── Shared primitives ─────────────────────────────────────────────────────────
const Card = ({ children, style = {} }) => (
  <div style={{ background: T.card, borderRadius: 14, border: `1px solid ${T.border}`, boxShadow: "0 4px 20px rgba(10,35,82,.07)", overflow: "hidden", fontFamily: FONT, ...style }}>
    {children}
  </div>
);

const inputBase = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: `1.5px solid ${T.border}`, fontSize: 15, color: T.text,
  background: "#fff", outline: "none", boxSizing: "border-box",
  transition: "border .18s, box-shadow .18s", fontFamily: "inherit",
};

const Input = ({ style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input {...props}
      style={{ ...inputBase, borderColor: focused ? T.orange : T.border, boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none", ...style }}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
    />
  );
};

const TextareaInput = ({ style = {}, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <textarea {...props}
      style={{ ...inputBase, borderColor: focused ? T.orange : T.border, boxShadow: focused ? `0 0 0 3px rgba(249,115,22,.13)` : "none", resize: "vertical", minHeight: 90, ...style }}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
    />
  );
};

const Btn = ({ children, variant = "primary", size = "md", icon: Icon, onClick, disabled, style = {} }) => {
  const [hovered, setHovered] = useState(false);
  const pad = size === "sm" ? "6px 13px" : "10px 22px";
  const fs  = size === "sm" ? 14 : 15;
  const variants = {
    primary:   { background: hovered ? T.orangeHover : T.orange,  color: "#fff",  border: "none" },
    secondary: { background: hovered ? T.light : "#f1f5f9",        color: T.navy,  border: `1.5px solid ${T.border}` },
    outline:   { background: "transparent", color: hovered ? T.orange : T.navy, border: `1.5px solid ${hovered ? T.orange : T.border}` },
    success:   { background: hovered ? "#15803d" : T.success,      color: "#fff",  border: "none" },
    danger:    { background: hovered ? "#b91c1c" : T.danger,       color: "#fff",  border: "none" },
    navy:      { background: hovered ? T.navyMid : T.navy,         color: "#fff",  border: "none" },
    ghost:     { background: hovered ? T.light : "transparent",    color: T.navy,  border: "none" },
  };
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 8, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", padding: pad, fontSize: fs, transition: "all .15s", opacity: disabled ? .6 : 1, fontFamily: "inherit", ...variants[variant], ...style }}
    >
      {Icon && <Icon size={size === "sm" ? 13 : 14} />}
      {children}
    </button>
  );
};

const Field = ({ label, required, children, hint }) => (
  <div style={{ marginBottom: 16, fontFamily: FONT }}>
    <label style={{ display: "block", fontSize: 17, fontWeight: 700, color: T.navy, marginBottom: 5, letterSpacing: .3, textTransform: "uppercase" }}>
      {label}{required && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
    </label>
    {children}
    {hint && <p style={{ fontSize: 13, margin: "4px 0 0" }}>{hint}</p>}
  </div>
);

// ── Date helpers ──────────────────────────────────────────────────────────────
// Use "DD MMM YYYY" (e.g. 14 Jun 2026) — unambiguous for a US/international
// audience (unlike DD/MM vs MM/DD) and easier to scan for older readers.
const fmtDate = (d) => {
  if (!d) return "TBA";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
};
// True only when fmtDate had a real value to format — lets callers dial back
// urgency styling (red countdowns, "closes in" badges) on placeholder dates.
const hasDate = (d) => Boolean(d);
const fmtDateRange = (from, to) => {
  const f = fmtDate(from), t = fmtDate(to);
  return f === t ? f : `${f} – ${t}`;
};

const csvCell = (value) =>
  `"${String(value ?? "").replace(/"/g, '""').replace(/\r?\n/g, " ")}"`;

const downloadCSV = (filename, headers, rows) => {
  const csv = [
    headers.map(csvCell).join(","),
    ...rows.map((row) => row.map(csvCell).join(",")),
  ].join("\n");

  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// ── Tab nav bar (shared across all screens) ───────────────────────────────────
const TAB_ITEMS = [
  { key: "listing",     label: "Event Listings", icon: Search       },
  { key: "myapps",      label: "Registrations",   icon: ClipboardList },
  { key: "invitations", label: "My Invitations",     icon: Bell         },
];

// Replace the entire TopTabBar component with:
const TopTabBar = ({ active, onChange, appCount, invCount }) => (
  <div style={{ display: "flex", gap: 4, marginBottom: 10, background: T.card, borderRadius: 12, padding: 5, border: `1px solid ${T.border}`, width: "fit-content" }}>
    {TAB_ITEMS.map(t => {
      const isActive = active === t.key || (t.key === "listing" && ["listing","details","apply"].includes(active));
      const Icon = t.icon;
      return (
        <button key={t.key} onClick={() => onChange(t.key)}
          style={{
            display: "flex", alignItems: "center", gap: 7, padding: "9px 18px",
            borderRadius: 9, border: "none", cursor: "pointer", fontFamily: "inherit",
            fontWeight: 700, fontSize: 15, transition: "all .18s",
            background: isActive ? `linear-gradient(135deg, ${T.navy}, ${T.navyMid})` : "transparent",
            color: isActive ? "#fff" : T.muted,
          }}>
          <Icon size={14} />
          {t.label}
          {t.key === "myapps" && appCount > 0 && (
            <span style={{ background: isActive ? "rgba(255,255,255,.25)" : T.orange, color: "#fff", borderRadius: 99, fontSize: 12, fontWeight: 800, padding: "1px 7px", marginLeft: 2 }}>
              {appCount}
            </span>
          )}
          {t.key === "invitations" && invCount > 0 && (
            <span style={{ background: isActive ? "rgba(255,255,255,.25)" : T.danger, color: "#fff", borderRadius: 99, fontSize: 12, fontWeight: 800, padding: "1px 7px", marginLeft: 2 }}>
              {invCount}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

// ── Breadcrumb ────────────────────────────────────────────────────────────────
const Breadcrumb = ({ items }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 18, fontSize: 17 }}>
    {items.map((item, i) => (
      <React.Fragment key={i}>
        {i > 0 && <ChevronRight size={12} color={T.border} />}
        {item.onClick ? (
          <button onClick={item.onClick}
            style={{ background: "none", border: "none", cursor: "pointer", color: i === items.length - 1 ? T.navy : T.muted, fontWeight: i === items.length - 1 ? 700 : 500, fontSize: 17, padding: 0, fontFamily: "inherit" }}>
            {item.label}
          </button>
        ) : (
          <span style={{ color: T.navy, fontWeight: 700 }}>{item.label}</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const DeadlineCountdown = ({ deadline }) => {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    const calc = () => {
      const diff = new Date(deadline) - new Date();
      if (diff <= 0) { setTimeLeft("Expired"); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTimeLeft(d > 0 ? `${d}d ${h}h left` : `${h}h ${m}m left`);
    };
    calc();
    const t = setInterval(calc, 60000);
    return () => clearInterval(t);
  }, [deadline]);

  const isUrgent = new Date(deadline) - new Date() < 86400000 * 2; // < 2 days
  return (
    <div style={{ textAlign: "center", padding: "8px 14px", borderRadius: 8, background: isUrgent ? T.danger + "10" : T.light, border: `1px solid ${isUrgent ? T.danger + "40" : T.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: isUrgent ? T.danger : T.muted, textTransform: "uppercase", letterSpacing: .5 }}>Deadline</div>
      <div style={{ fontSize: 15, fontWeight: 900, color: isUrgent ? T.danger : T.navy, marginTop: 2 }}>{timeLeft}</div>
    </div>
  );
};

// ── File / image helpers ──────────────────────────────────────────────────────
// Files (banners, institution logos, campus photos) are stored by filename only
// (e.g. school_profile.school_logo, events.banner_file_name). They are served
// from the same static path your upload pipeline writes to — matches
// DOWNLOAD_BASE in SchoolRegistration.jsx. webp/png/jpg all render fine via <img>.
const FILE_BASE_URL =`${baseUrl}/api/v2/user_modules/serve-pdf/`;
const getFileUrl = (filename) => {
  if (!filename) return null;
  if (/^https?:\/\//i.test(filename)) return filename;
  return `${FILE_BASE_URL}${filename}`;
};

const getInitials = (name = "") => {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "EP";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

// ── Time helper (12-hour, friendly format) ────────────────────────────────────
const fmtTime = (t) => {
  if (!t) return null;
  const [h, m] = String(t).split(":");
  const hour = parseInt(h, 10);
  if (isNaN(hour)) return t;
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:${m || "00"} ${period}`;
};

// ── Agenda parser — reads events.event_agenda (JSON column) ──────────────────
// Accepts an array of objects in a few common shapes and normalizes them to
// { time, title, speaker }. Returns [] if the column is empty/unparseable so
// the Agenda tab can show an empty state instead of placeholder content.
const parseAgenda = (raw) => {
  if (!raw) return [];

  let data = raw;

  if (typeof raw === "string") {
    try {
      data = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(data)) return [];

  return data.map(item => ({
    date: item.item_date,
    startTime: item.start_time,
    endTime: item.end_time,
    title: item.title,
    description: item.description || ""
  }));
};

// ── Seats-left / urgency helpers ──────────────────────────────────────────────
// "Seats Left" = max_applicants − current_applications (per events table)
const getSeatsLeft = (ev) => {
  const max = Number(ev?.max_applicants || 0);
  if (!max) return null; // no cap configured for this event
  const used = Number(ev?.current_applications || 0);
  return Math.max(max - used, 0);
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  return (new Date(dateStr) - new Date()) / 86400000;
};

// ── WhatsApp share link (generic share — no fixed phone number required) ──────
const getWhatsAppShareUrl = (ev) => {
  const lines = [
    `📢 ${ev.event_name}`,
    ev.school_name ? `Hosted by: ${ev.school_name}` : null,
    `📍 ${ev.city}, ${ev.country}`,
    `📅 ${fmtDateRange(ev.event_start_date, ev.event_end_date)}`,
    ev.reg_close_date ? `⏰ Register by: ${fmtDate(ev.reg_close_date)}` : null,
    "",
    "View & register on EduPlatform.",
  ].filter(Boolean);
  return `https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`;
};

// ── Tag chip — for "Free", category, mode etc. ────────────────────────────────
const Tag = ({ children, tone = "neutral", icon: Icon }) => {
  const tones = {
    free:    { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" },
    info:    { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
    neutral: { bg: "#f1f5f9", color: T.navy,    border: T.border },
    accent:  { bg: "#fff7ed", color: T.orangeHover, border: "#fed7aa" },
  };
  const s = tones[tone] || tones.neutral;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: 6, padding: "4px 12px", fontSize: 14, fontWeight: 700,
      letterSpacing: .2, whiteSpace: "nowrap",
    }}>
      {Icon && <Icon size={13} />}
      {children}
    </span>
  );
};

// ── Institution logo avatar (with graceful initials fallback) ─────────────────
const LogoAvatar = ({ logo, name, size = 44, radius = 10, bg }) => {
  const url = getFileUrl(logo);
  const [errored, setErrored] = useState(false);
  if (url && !errored) {
    return (
      <img src={url} alt={name ? `${name} logo` : "Institution logo"} onError={() => setErrored(true)}
        style={{ width: size, height: size, borderRadius: radius, objectFit: "contain", background: "#fff", border: `1px solid ${T.border}`, flexShrink: 0, padding: 3, boxSizing: "border-box" }} />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: radius, background: bg || T.navy, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: Math.round(size * 0.36), flexShrink: 0, border: `1px solid ${T.border}` }}>
      {getInitials(name)}
    </div>
  );
};


// ── "Seats Left" FOMO badge ────────────────────────────────────────────────────
const SeatsLeftBadge = ({ seatsLeft, size = "md" }) => {
  if (seatsLeft === null || seatsLeft === undefined) return null;
  const sold   = seatsLeft <= 0;
  const urgent = !sold && seatsLeft <= 5;
  const fs = size === "sm" ? 13 : 13.5;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: sold ? "#f3f4f6" : urgent ? "#fff7ed" : "#f0fdf4",
      color:      sold ? T.muted   : urgent ? T.orangeHover : "#15803d",
      border: `1px solid ${sold ? T.border : urgent ? "#fed7aa" : "#bbf7d0"}`,
      borderRadius: 99, padding: "4px 11px", fontSize: fs, fontWeight: 800,
      whiteSpace: "nowrap",
    }}>
      <Users size={13} />
      {sold ? "Fully booked" : `Only ${seatsLeft} seat${seatsLeft === 1 ? "" : "s"} left`}
    </span>
  );
};

// ── Compact registration countdown badge (for cards & sidebars) ───────────────
const MiniCountdown = ({ deadline, label = "Closes in" }) => {
  const [text, setText] = useState("");

  useEffect(() => {
    if (!deadline) return;
    const calc = () => {
      const diff = new Date(deadline) - new Date();
      if (diff <= 0) { setText("Closed"); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setText(d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    calc();
    const t = setInterval(calc, 60000);
    return () => clearInterval(t);
  }, [deadline]);

  if (!deadline) return null;
  const diffDays = daysUntil(deadline);
  const closed = diffDays !== null && diffDays <= 0;
  const urgent = diffDays !== null && diffDays > 0 && diffDays <= 2;

  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: closed ? "#f3f4f6" : urgent ? "#fef2f2" : "#eff6ff",
      color:      closed ? T.muted   : urgent ? T.danger   : T.accent,
      border: `1px solid ${closed ? T.border : urgent ? "#fecaca" : "#bfdbfe"}`,
      borderRadius: 99, padding: "4px 12px", fontSize: 14, fontWeight: 800,
      whiteSpace: "nowrap",
    }}>
      <CalendarClock size={13} />
      {closed ? "Registration closed" : `${label} ${text}`}
    </span>
  );
};

// ── Campus / institution photo strip ───────────────────────────────────────────
const PhotoStrip = ({ photos, title = "Campus Photos" }) => {
  let list = [];
  try {
    list = typeof photos === "string" ? JSON.parse(photos) : (photos || []);
  } catch { list = []; }
  list = (Array.isArray(list) ? list : []).filter(Boolean).slice(0, 6);
  if (list.length === 0) return null;
  return (
    <Card style={{ padding: 20, marginBottom: 18 }}>
      <div style={{ fontWeight: 800, fontSize: 15, color: T.navy, marginBottom: 14, display: "flex", alignItems: "center", gap: 7 }}>
        <ImageIcon size={14} color={T.orange} /> {title}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
        {list.map((p, i) => (
          <img key={i} src={getFileUrl(p)} alt={`${title} ${i + 1}`}
            style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 8, border: `1px solid ${T.border}` }}
            onError={(e) => { e.target.style.display = "none"; }} />
        ))}
      </div>
    </Card>
  );
};

// ── WhatsApp share button ──────────────────────────────────────────────────────
const WhatsAppButton = ({ ev, size = "sm", full }) => (
  <Btn variant="outline" size={size} icon={MessageCircle}
    style={{ color: "#15803d", borderColor: "#bbf7d0", ...(full ? { flex: 1, justifyContent: "center" } : {}) }}
    onClick={() => window.open(getWhatsAppShareUrl(ev), "_blank")}>
    Share on WhatsApp
  </Btn>
);

// ── Quick (one-click) registration modal ───────────────────────────────────────
function QuickRegisterModal({ ev, onClose, onSubmitSuccess, onGoToMyApps }) {
  const uc = getUserCredentials();
  const [numReps, setNumReps]       = useState(2);
  const [notes, setNotes]           = useState("");
  const [agreed, setAgreed]         = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [error, setError]           = useState("");

  if (!ev) return null;

  const handleSubmit = async () => {
    if (!agreed) { setError("Please confirm the declaration to continue."); return; }
    setSubmitting(true);
    setError("");
    try {
      const creds = CreateData();
      const now   = getFormattedDate();
      await axios.post(creds[1], {
        database: "edu_platform",
        table: "event_applications",
        formData: {
          event_id:            ev.primary_id,
          applicant_user_id:   uc.user_id,
          applicant_entity_id: uc.selectedentity || uc.entities_id,
          tenant_id:           uc.tenants_id,
          group_id:            uc.groups_id,
          num_representatives: numReps,
          additional_notes:    notes || null,
          application_status:  "applied",
          applied_on:          now,
          created_by:          uc.user_id,
          created_date:        now,
          modified_date:       now,
          is_active:           1,
        },
      }, creds[0]);
      setSubmitted(true);
      onSubmitSuccess(ev.primary_id);
    } catch (err) {
      const errorStr = err?.response?.data?.error || "";
      console.error("Quick register error:", err?.response);
      if (errorStr.includes("1062") || errorStr.toLowerCase().includes("duplicate entry")) {
        setAlreadyApplied(true);
        onSubmitSuccess(ev.primary_id);
      } else {
        setError(err?.response?.data?.Message || "Submission failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const bannerColor = getBannerColor(ev.primary_id);
  const seatsLeft   = getSeatsLeft(ev);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(10,35,82,.55)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      fontFamily: FONT,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 460, maxHeight: "92vh", overflowY: "auto" }}>
        <Card style={{ overflow: "hidden" }}>
          {/* Header */}
          <div style={{ background: `linear-gradient(135deg, ${T.navy}, ${T.navyMid})`, padding: "16px 18px", display: "flex", alignItems: "center", gap: 12 }}>
            <LogoAvatar logo={ev.institution_logo} name={ev.school_name} size={42} bg={bannerColor} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "#fff", fontWeight: 900, fontSize: 17, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.event_name}</div>
              <div style={{ color: "rgba(255,255,255,.8)", fontSize: 17.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.school_name}</div>
            </div>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: 8, padding: 6, cursor: "pointer", color: "#fff", flexShrink: 0 }}>
              <X size={16} />
            </button>
          </div>

          <div style={{ padding: 20 }}>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div style={{ width: 56, height: 56, background: T.success + "20", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                  <CheckCircle size={28} color={T.success} />
                </div>
                <h3 style={{ color: T.navy, margin: "0 0 8px", fontSize: 19 }}>Registration Submitted!</h3>
                <p style={{ fontSize: 20, lineHeight: 1.6, margin: "0 0 18px", color: T.text }}>
                  You're registered for <strong>{ev.event_name}</strong>. We'll notify you about next steps.
                </p>
                <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                  <Btn variant="outline" onClick={onClose}>Close</Btn>
                  <Btn variant="primary" icon={ClipboardList} onClick={onGoToMyApps}>My Applications</Btn>
                </div>
              </div>
            ) : alreadyApplied ? (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div style={{ width: 56, height: 56, background: T.purple + "18", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                  <CheckCircle size={28} color={T.purple} />
                </div>
                <h3 style={{ color: T.navy, margin: "0 0 8px", fontSize: 19 }}>Already Registered</h3>
                <p style={{ fontSize: 20, lineHeight: 1.6, margin: "0 0 18px", color: T.text }}>
                  Your institution has already registered for this event.
                </p>
                <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                  <Btn variant="outline" onClick={onClose}>Close</Btn>
                  <Btn variant="primary" icon={ClipboardList} onClick={onGoToMyApps}>My Applications</Btn>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                  <Tag tone="free" icon={CheckCircle}>Free to Apply</Tag>
                  {ev.event_category && <Tag tone="info">{ev.event_category}</Tag>}
                  {ev.event_mode && <Tag tone="neutral">{ev.event_mode}</Tag>}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                  <div style={{ padding: 11, background: T.bg, borderRadius: 8 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: .4, color: T.muted }}>Event Dates</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginTop: 3 }}>{fmtDateRange(ev.event_start_date, ev.event_end_date)}</div>
                  </div>
                  <div style={{ padding: 11, background: T.bg, borderRadius: 8 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: .4, color: T.muted }}>Last Date to Register</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: hasDate(ev.reg_close_date) ? T.danger : T.muted, marginTop: 3 }}>{fmtDate(ev.reg_close_date)}</div>
                  </div>
                </div>

                {seatsLeft !== null && (
                  <div style={{ marginBottom: 16 }}><SeatsLeftBadge seatsLeft={seatsLeft} /></div>
                )}

                <Field label="Number of Representatives" required hint="Max 2 representatives per institution">
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <button onClick={() => setNumReps(p => Math.max(1, p - 1))}
                      style={{ width: 36, height: 36, borderRadius: 8, border: `1.5px solid ${T.border}`, background: T.light, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Minus size={14} />
                    </button>
                    <span style={{ fontSize: 38, fontWeight: 900, color: T.navy, minWidth: 28, textAlign: "center" }}>{numReps}</span>
                    <button onClick={() => setNumReps(p => Math.min(2, p + 1))}
                      style={{ width: 36, height: 36, borderRadius: 8, border: `1.5px solid ${T.border}`, background: T.light, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Plus size={14} />
                    </button>
                  </div>
                </Field>

                <Field label="Notes for organizer (optional)">
                  <TextareaInput value={notes} onChange={e => setNotes(e.target.value)}
                    placeholder="Anything the organizer should know…" style={{ minHeight: 70 }} />
                </Field>

                <label style={{ display: "flex", gap: 10, cursor: "pointer", alignItems: "flex-start", marginBottom: 16 }}>
                  <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: 3 }} />
                  <span style={{ fontSize: 17.5, color: T.text, lineHeight: 1.6 }}>
                    I confirm our institution's profile details are accurate and agree to this event's participation terms.
                  </span>
                </label>

                {error && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, padding: "10px 14px", background: "rgba(220,38,38,.07)", borderRadius: 8, border: `1px solid rgba(220,38,38,.25)` }}>
                    <AlertCircle size={14} color={T.danger} />
                    <span style={{ fontSize: 17.5, color: T.danger }}>{error}</span>
                  </div>
                )}

                <div style={{ display: "flex", gap: 10 }}>
                  <Btn variant="outline" style={{ flex: 1, justifyContent: "center" }} onClick={onClose}>Cancel</Btn>
                  <Btn variant="primary" style={{ flex: 1.4, justifyContent: "center" }} icon={Send} onClick={handleSubmit} disabled={submitting}>
                    {submitting ? "Submitting…" : "Confirm Registration"}
                  </Btn>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ── Floating "Register Now" button (mobile-friendly sticky CTA) ───────────────
const FloatingRegisterButton = ({ onClick, label = "Register Now" }) => (
  <button onClick={onClick} style={{
    position: "fixed", bottom: 22, right: 22, zIndex: 900,
    display: "flex", alignItems: "center", gap: 8,
    background: T.orange, color: "#fff", border: "none",
    borderRadius: 99, padding: "14px 24px", fontSize: 16, fontWeight: 800,
    boxShadow: "0 8px 24px rgba(249,115,22,.4)", cursor: "pointer",
    fontFamily: FONT, letterSpacing: .3,
  }}>
    <Zap size={16} />
    {label}
  </button>
);

// ══════════════════════════════════════════════════════════════════════════════
// SCREEN 1 — EVENT LISTING
// ══════════════════════════════════════════════════════════════════════════════
function EventListing({ events, loading, onViewDetails, onApply, onQuickRegister, savedIds, onToggleSave, appliedEventIds }) {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
  country: "",
  state: "",
  city: "",
  school_name: "",
  event_category: "",
  event_status: ""
});
  const [gridView, setGridView] = useState(true);

const filtered = events.filter(ev => {
  const q = search.toLowerCase();

  return (
    (!q ||
      (ev.event_name || "").toLowerCase().includes(q) ||
      (ev.school_name || "").toLowerCase().includes(q) ||
      (ev.city || "").toLowerCase().includes(q) ||
      (ev.state || "").toLowerCase().includes(q) ||
      (ev.country || "").toLowerCase().includes(q)
    ) &&

    (!filters.country ||
      ev.country === filters.country) &&

    (!filters.state ||
      ev.state === filters.state) &&

    (!filters.city ||
      ev.city === filters.city) &&

    (!filters.school_name ||
      ev.school_name === filters.school_name) &&

    (!filters.event_category ||
      ev.event_category === filters.event_category) &&

    (!filters.event_status ||
      ev.event_status === filters.event_status)
  );
});

const countries = [
  ...new Set(events.map(e => e.country).filter(Boolean))
];

const states = [
  ...new Set(
    events
      .filter(
        e =>
          !filters.country ||
          e.country === filters.country
      )
      .map(e => e.state)
      .filter(Boolean)
  )
];

const cities = [
  ...new Set(
    events
      .filter(
        e =>
          (!filters.country || e.country === filters.country) &&
          (!filters.state || e.state === filters.state)
      )
      .map(e => e.city)
      .filter(Boolean)
  )
];

const schools = [
  ...new Set(
    events
      .filter(
        e =>
          (!filters.country || e.country === filters.country) &&
          (!filters.state || e.state === filters.state) &&
          (!filters.city || e.city === filters.city)
      )
      .map(e => e.school_name)
      .filter(Boolean)
  )
];

const categories = [
  ...new Set(events.map(e => e.event_category).filter(Boolean))
];

const statuses = [
  ...new Set(events.map(e => e.event_status).filter(Boolean))
];

  return (
    <div style={{ fontFamily: FONT }}>

<Card style={{ padding: "11px 16px", marginBottom: 10 }}>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      width: "100%"
    }}
  >
    {/* Filters */}
    <div
      style={{
        display: "flex",
        gap: 10,
        flexWrap: "wrap"
      }}
    >
      {/* all your select boxes */}
      
  <select
    value={filters.country}
    onChange={(e) =>
      setFilters({
        ...filters,
        country: e.target.value,
        state: "",
        city: "",
        school_name: ""
      })
    }
    style={{ ...inputBase, width:160 }}
  >
    <option value="">All</option>
    {countries.map(c => (
      <option key={c} value={c}>{c}</option>
    ))}
  </select>

  <select
    value={filters.state}
    onChange={(e) =>
      setFilters({
        ...filters,
        state: e.target.value,
        city: "",
        school_name: ""
      })
    }
    style={{ ...inputBase, width:160 }}
  >
    <option value="">All</option>
    {states.map(s => (
      <option key={s} value={s}>{s}</option>
    ))}
  </select>

  <select
    value={filters.city}
    onChange={(e) =>
      setFilters({
        ...filters,
        city: e.target.value,
        school_name: ""
      })
    }
    style={{ ...inputBase, width:160 }}
  >
    <option value="">All</option>
    {cities.map(c => (
      <option key={c} value={c}>{c}</option>
    ))}
  </select>

  <select
    value={filters.school_name}
    onChange={(e) =>
      setFilters({
        ...filters,
        school_name: e.target.value
      })
    }
    style={{ ...inputBase, width:220 }}
  >
    <option value="">All</option>
    {schools.map(s => (
      <option key={s} value={s}>{s}</option>
    ))}
  </select>

  <select
    value={filters.event_category}
    onChange={(e) =>
      setFilters({
        ...filters,
        event_category: e.target.value
      })
    }
    style={{ ...inputBase, width:160 }}
  >
    <option value="">All</option>
    {categories.map(c => (
      <option key={c} value={c}>{c}</option>
    ))}
  </select>

  <select
    value={filters.event_status}
    onChange={(e) =>
      setFilters({
        ...filters,
        event_status: e.target.value
      })
    }
    style={{ ...inputBase, width:160 }}
  >
    <option value="">All</option>
    {statuses.map(s => (
      <option key={s} value={s}>{s}</option>
    ))}
  </select>

    </div>

    {/* Search pushed fully right */}
    <div
      style={{
        marginLeft: "auto",
        position: "relative",
        width: 320,
        minWidth: 320
      }}
    >
      <Search
        size={14}
        color={T.muted}
        style={{
          position: "absolute",
          left: 10,
          top: "50%",
          transform: "translateY(-50%)"
        }}
      />

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search events or cities..."
        style={{
          ...inputBase,
          paddingLeft: 32,
          width: "100%"
        }}
      />
    </div>
  </div>
</Card>



      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 280, gap: 12 }}>
          <div style={{ width: 32, height: 32, border: `4px solid ${T.light}`, borderTop: `4px solid ${T.orange}`, borderRadius: "50%", animation: "spin .8s linear infinite" }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <span style={{ color: T.muted, fontSize: 15 }}>Loading events…</span>
        </div>
      ) : filtered.length === 0 ? (
        <Card style={{ padding: 60, textAlign: "center" }}>
          <Search size={32} color={T.border} style={{ marginBottom: 12 }} />
          <div style={{ fontWeight: 700 }}>No events found</div>
          <div style={{ fontSize: 17, marginTop: 4 }}>Try adjusting your search or filters</div>
        </Card>
      ) : (
        <div style={{
          display: gridView ? "grid" : "flex",
          gridTemplateColumns: "repeat(auto-fill, minmax(450px, 1fr))",
          flexDirection: "column",
          gap: 18,
        }}>
          {filtered.map(ev => {
            const bannerColor = getBannerColor(ev.primary_id);
            const isSaved     = savedIds[ev.primary_id];
            const isApplied   = appliedEventIds.has(ev.primary_id);
            const seatsLeft   = getSeatsLeft(ev);
            const bannerUrl   = getFileUrl(ev.banner_file_name);
            return (
              <Card key={ev.primary_id} style={{ overflow: "hidden", transition: "box-shadow .2s",height:495 }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 8px 30px rgba(10,35,82,.14)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "0 4px 20px rgba(10,35,82,.07)"}>
                {/* Banner — uses the uploaded event banner when available, falls back to a brand-navy gradient.
                    A square logo badge sits in the corner of the banner; the event name moves below it. */}
                <div style={{
                  height: 160, position: "relative",
                  background: bannerUrl
                    ? `url(${bannerUrl}) center/cover no-repeat`
                    : `linear-gradient(135deg, ${bannerColor} 0%, ${bannerColor}bb 100%)`,
                }}>
                  {bannerUrl && (
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(10,35,82,.40) 0%, rgba(10,35,82,0) 45%)" }} />
                  )}
                  <div style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 6 }}>
                    {isApplied && (
                      <span style={{ background: T.success, color: "#fff", padding: "3px 10px", borderRadius: 99, fontSize: 13, fontWeight: 800, letterSpacing: .4, display: "flex", alignItems: "center", gap: 4 }}>
                        <Check size={10} /> REGISTERED
                      </span>
                    )}
                    {/* <StatusBadge status={ev.event_status} /> */}
                  </div>
                  {/* Square logo badge — overlaps the banner's bottom edge */}
                  <div style={{
                    position: "absolute", bottom: -24, left: 16,
                    width: 64, height: 64, borderRadius: 12, background: "#fff",
                    border: `1px solid ${T.border}`, boxShadow: "0 4px 14px rgba(10,35,82,.18)",
                    display: "flex", alignItems: "center", justifyContent: "center", padding: 4, boxSizing: "border-box",
                  }}>
                    <LogoAvatar logo={ev.institution_logo} name={ev.school_name} size={56} radius={8} bg={bannerColor} />
                  </div>
                </div>

                <div style={{ padding: "34px 14px 14px" }}>
                  {/* Event name — large, sits below the banner for readability */}
                  <div style={{ fontSize: 22, fontWeight: 900, color: T.navy, lineHeight: 1.3, marginBottom: 4 }}>
                    {ev.event_name}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.muted, marginBottom: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    Hosted by {ev.school_name || "EduPlatform Partner Institution"}
                  </div>

                  {/* Quick-scan tags */}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                    <Tag tone="free" icon={CheckCircle}>Free to Apply</Tag>
                    {ev.event_category && <Tag tone="info">{ev.event_category}</Tag>}
                    {ev.event_mode && <Tag tone="neutral">{ev.event_mode}</Tag>}
                  </div>

                  {/* FOMO row — seats left + registration countdown */}
                  {(seatsLeft !== null || ev.reg_close_date) && (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                      <SeatsLeftBadge seatsLeft={seatsLeft} />
                      <MiniCountdown deadline={ev.reg_close_date} label="Register by" />
                    </div>
                  )}

                  {/* Key facts — location, date(s), time, last date to register */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 14, padding: "11px 12px", background: T.bg, borderRadius: 8 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 15, color: T.text, fontWeight: 600 }}>
                      <MapPin size={13} color={T.navy} />{ev.city}, {ev.country}
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 15, color: T.text, fontWeight: 600 }}>
                      <Calendar size={13} color={T.navy} />{fmtDateRange(ev.event_start_date, ev.event_end_date)}
                    </span>
                    {(ev.event_time || ev.reg_open_time) && (
                      <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 15, color: T.text, fontWeight: 600 }}>
                        <Clock size={13} color={T.navy} />{fmtTime(ev.event_time || ev.reg_open_time)}
                      </span>
                    )}
                    <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 15, color: T.danger, fontWeight: 700 }}>
                      <AlertCircle size={13} />Last date to register: {fmtDate(ev.reg_close_date)}
                    </span>
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", gap: 7, marginBottom: 8 }}>
                    <Btn variant="outline" size="sm" style={{ flex: 1 }} icon={Eye} onClick={() => onViewDetails(ev)}>Details</Btn>
                    <WhatsAppButton ev={ev} full />
                    {isApplied ? (
                      <Btn variant="ghost" size="sm" style={{ flex: 1.5, background: T.success + "15", color: T.success, cursor: "default" }} icon={CheckCircle}>Registered</Btn>
                    ) : (
                      <Btn variant="primary" size="sm" style={{ flex: 1.5 }} icon={Zap} onClick={() => onQuickRegister(ev)}>Register Now</Btn>
                    )}
                    <button onClick={() => onToggleSave(ev.primary_id)}
                      style={{ background: isSaved ? T.warn + "18" : T.light, border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: isSaved ? T.warn : T.muted }}>
                      <Star size={13} fill={isSaved ? T.warn : "none"} />
                    </button>
                  </div>
                  
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MyInvitations({ invitations, loading, onRefresh, onRespondSuccess, uc }) {
  const [actionLoading, setActionLoading] = useState(null);
  const [filter, setFilter]               = useState("pending");

  const pending   = invitations.filter(i => i.invitation_status === "pending");
  const responded = invitations.filter(i => i.invitation_status !== "pending");
  const displayed = filter === "pending" ? pending : responded;

  const handleAction = async (inv, action) => {
    setActionLoading({ id: inv.primary_id, action });
    try {
      const creds = Fetchupdatequery();
      const now   = getFormattedDate();
      await axios.post(creds[1], {
        schema_name: "edu_platform",
        table: "event_invitations",
        set_column: [{
          invitation_status: action === "accept" ? "accepted" : "declined",
          responded_on: now,
          modified_date: now,
        }],
        where: [{
          primary_id: inv.primary_id,
          applicant_entity_id: uc.selectedentity || uc.entities_id,
        }],
      }, creds[0]);
      onRespondSuccess(); // refresh from parent
    } catch (err) {
      console.error("Invitation response error:", err);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div style={{ fontFamily: FONT }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
      </div>

      {/* Filter pills */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {[
          { key: "pending",   label: `Pending (${pending.length})`     },
          { key: "responded", label: `Responded (${responded.length})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{
              background: filter === f.key ? T.navy : T.card,
              color:      filter === f.key ? "#fff" : T.text,
              border: `1px solid ${T.border}`, borderRadius: 99,
              padding: "5px 16px", fontSize: 17, fontWeight: 700,
              cursor: "pointer", transition: "all .15s",
            }}>
            {f.label}
          </button>
        ))}
          <Btn variant="outline" size="sm" icon={RefreshCw} onClick={onRefresh}>Refresh</Btn>
      
      </div>

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 240, gap: 12 }}>
          <div style={{ width: 32, height: 32, border: `4px solid ${T.light}`, borderTop: `4px solid ${T.orange}`, borderRadius: "50%", animation: "spin .8s linear infinite" }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <span style={{ color: T.muted, fontSize: 15 }}>Loading invitations…</span>
        </div>
      ) : displayed.length === 0 ? (
        <Card style={{ padding: 60, textAlign: "center" }}>
          <Bell size={32} color={T.border} style={{ marginBottom: 12 }} />
          <div style={{ fontWeight: 700 }}>
            {filter === "pending" ? "No pending invitations" : "No past invitations"}
          </div>
          <div style={{ fontSize: 17, marginTop: 4 }}>
            {filter === "pending" ? "You're all caught up! New invitations will appear here." : "Responded invitations will appear here."}
          </div>
        </Card>
      ) : filter === "pending" ? (
        /* ── Pending cards ── */
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {displayed.map(inv => (
            <Card key={inv.primary_id} style={{ padding: 0, overflow: "hidden", border: `1.5px solid ${T.accent}30` }}>
              <div style={{ display: "flex" }}>
                {/* Left accent stripe */}
                <div style={{ width: 6, background: `linear-gradient(180deg, ${T.accent}, ${T.purple})`, flexShrink: 0 }} />
                <div style={{ flex: 1, padding: 20 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: T.navy }}>{inv.event_name}</h3>
                        <div
                          style={{
                            fontSize: 17,
                            color: T.accent,
                            fontWeight: 700,
                            marginTop: 4
                          }}
                        >
                          <Building size={11} style={{ marginRight: 4 }} />
                          {inv.school_name}
                        </div>
                        <StatusBadge status={inv.invitation_status} />
                      </div>
                      <div style={{ fontSize: 17, marginBottom: 10 }}>
                        {inv.city}, {inv.country}
                      </div>
                      <div style={{ display: "flex", gap: 18, fontSize: 17, flexWrap: "wrap" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <Bell size={11} /> Invited: {fmtDate(inv.invited_on)}
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <Calendar size={11} /> Event: {fmtDateRange(inv.event_start_date, inv.event_end_date)}
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: 4, color: T.danger }}>
                          <AlertCircle size={11} /> Deadline: {fmtDate(inv.acceptance_deadline)}
                        </span>
                      </div>
                    </div>
                    {/* Deadline countdown */}
                    <DeadlineCountdown deadline={inv.acceptance_deadline} />
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                    <Btn variant="success" size="sm" icon={CheckCircle}
                      onClick={() => handleAction(inv, "accept")}
                      disabled={!!actionLoading}>
                      {actionLoading?.id === inv.primary_id && actionLoading?.action === "accept"
                        ? "Accepting…" : "Accept Invitation"}
                    </Btn>
                    <Btn variant="danger" size="sm" icon={X}
                      onClick={() => handleAction(inv, "reject")}
                      disabled={!!actionLoading}>
                      {actionLoading?.id === inv.primary_id && actionLoading?.action === "reject"
                        ? "Declining…" : "Decline"}
                    </Btn>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        /* ── Responded table ── */
        <Card style={{ overflow: "visible" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${T.navy}, ${T.navyMid})` }}>
                  {["Event Name", "Location", "Event Dates", "Invited On", "Deadline", "Response", "Responded On"].map(h => (
                    <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "rgb(255, 255, 255)", textTransform: "uppercase", letterSpacing: .5, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayed.map((inv, i) => (
                  <tr key={inv.primary_id}
                    style={{ borderTop: `1px solid ${T.border}`, background: i % 2 === 0 ? "#fff" : T.bg + "88" }}>
                    <td style={{ padding: "12px 14px", fontSize: 15, fontWeight: 700, color: T.navy }}>{inv.event_name}</td>
                    <td style={{ padding: "12px 14px", fontSize: 17, whiteSpace: "nowrap" }}>{inv.city}, {inv.country}</td>
                    <td style={{ padding: "12px 14px", fontSize: 17, whiteSpace: "nowrap" }}>{fmtDateRange(inv.event_start_date, inv.event_end_date)}</td>
                    <td style={{ padding: "12px 14px", fontSize: 17, whiteSpace: "nowrap" }}>{fmtDate(inv.invited_on)}</td>
                    <td style={{ padding: "12px 14px", fontSize: 17, whiteSpace: "nowrap" }}>{fmtDate(inv.acceptance_deadline)}</td>
                    <td style={{ padding: "12px 14px" }}><StatusBadge status={inv.invitation_status} /></td>
                    <td style={{ padding: "12px 14px", fontSize: 17, whiteSpace: "nowrap" }}>{fmtDate(inv.responded_on)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
// ══════════════════════════════════════════════════════════════════════════════
// SCREEN 2 — EVENT DETAILS
// ══════════════════════════════════════════════════════════════════════════════
function EventDetails({ ev, onBack, onApply, onQuickRegister, saved, onToggleSave, isApplied }) {
  const [activeTab, setActiveTab] = useState("overview");
  const tabs = ["overview", "agenda", "venue"];

  const reviewDate = (ev.review_start_date || ev.review_end_date)
    ? `${fmtDate(ev.review_start_date)} → ${fmtDate(ev.review_end_date)}`
    : "TBA";

  const timeline = [
    { label: "Reg. Opens",    date: fmtDate(ev.reg_open_date),           status: "completed", icon: CheckCircle, color: T.success },
    { label: "Reg. Closes",   date: fmtDate(ev.reg_close_date),          status: "active",    icon: Clock,       color: T.orange  },
    { label: "Review",        date: reviewDate, status: "upcoming", icon: Eye },
    { label: "Invitations",   date: fmtDate(ev.invitation_release_date), status: "upcoming",  icon: Send,        color: T.muted   },
    { label: "Event Day",     date: fmtDateRange(ev.event_start_date, ev.event_end_date), status: "upcoming", icon: Flag },
  ];

  // Agenda comes from events.event_agenda (JSON column) — empty if not yet set
  const agenda = parseAgenda(ev.event_agenda);

  const bannerColor = getBannerColor(ev.primary_id);
  const bannerUrl   = getFileUrl(ev.banner_file_name);
  const seatsLeft   = getSeatsLeft(ev);

  return (
    <div style={{maxWidth: 1600,margin: "0 auto",width: "100%"}}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 20, alignItems: "start" }}>
        {/* Left column — hero banner starts here, flush with the top */}
        <div>
          {/* Hero banner — event photo (or brand gradient) on top; host details sit
              below it on a clean background for reliable readability over any image. */}
          <Card style={{ overflow: "hidden", marginBottom: 20 }}>
            <div style={{
              position: "relative", height: 240,
              background: bannerUrl
                ? `url(${bannerUrl}) center/cover no-repeat`
                : `linear-gradient(135deg, ${bannerColor} 0%, ${T.navy} 100%)`,
            }}>
              {bannerUrl && (
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(10,35,82,.35) 0%, rgba(10,35,82,0) 45%)" }} />
              )}
            </div>

            {/* Title row — square logo badge overlaps the banner; name & host sit beside it */}
            <div style={{ padding: "0 24px 18px", display: "flex", alignItems: "flex-end", gap: 18, flexWrap: "wrap" }}>
              <div style={{
                width: 92, height: 92, borderRadius: 14, background: "#fff",
                border: `1px solid ${T.border}`, boxShadow: "0 6px 18px rgba(10,35,82,.2)",
                display: "flex", alignItems: "center", justifyContent: "center", padding: 6, boxSizing: "border-box",
                marginTop: -46, flexShrink: 0,
              }}>
                <LogoAvatar logo={ev.institution_logo} name={ev.school_name} size={80} radius={10} bg={bannerColor} />
              </div>
              <div style={{ flex: 1, minWidth: 240, paddingTop: 14 }}>
                {ev.event_category && (
                  <div style={{ fontSize: 13, color: T.orangeHover, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>{ev.event_category}</div>
                )}
                <h1 style={{ fontSize: 28, fontWeight: 900, color: T.navy, margin: "0 0 6px", lineHeight: 1.25 }}>{ev.event_name}</h1>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14.5, color: T.text, fontWeight: 700 }}>
                  <Building size={14} color={T.orange} /> Hosted by {ev.school_name || "EduPlatform Partner Institution"}
                </div>
              </div>
            </div>

            {/* Tags + FOMO strip */}
            <div style={{ padding: "14px 20px", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", borderTop: `1px solid ${T.border}` }}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <Tag tone="free" icon={CheckCircle}>Free to Apply</Tag>
                {ev.event_category && <Tag tone="info">{ev.event_category}</Tag>}
                {ev.event_mode && <Tag tone="neutral">{ev.event_mode}</Tag>}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <SeatsLeftBadge seatsLeft={seatsLeft} />
                <MiniCountdown deadline={ev.reg_close_date} label="Register by" />
              </div>
            </div>
          </Card>
        </div>

        {/* Right column — Back button sits at the same height as the banner top, directly above Registration */}
        <div>
          <Btn variant="outline" icon={ArrowLeft} size="sm" onClick={onBack} style={{ marginBottom: 12, width: "100%", justifyContent: "center" }}>
            Back to Event Listings
          </Btn>

          {/* Registration card — primary conversion point */}
          <Card style={{ padding: 16, marginBottom: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: T.navy, marginBottom: 12, textTransform: "uppercase", letterSpacing: .5 }}>Registration</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
              {seatsLeft !== null && <SeatsLeftBadge seatsLeft={seatsLeft} />}
              {ev.reg_close_date && <MiniCountdown deadline={ev.reg_close_date} label="Register by" />}
            </div>
            {isApplied ? (
              <div style={{ width: "100%", padding: "10px 0", textAlign: "center", background: T.success + "12", borderRadius: 8, border: `1px solid ${T.success}33`, color: T.success, fontWeight: 700, fontSize: 14.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <CheckCircle size={14} /> Registration Submitted
              </div>
            ) : (
              <Btn variant="primary" style={{ width: "100%", justifyContent: "center" }} icon={Zap} onClick={() => onQuickRegister(ev)}>Register Now</Btn>
            )}
            <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
              <Btn variant="secondary" size="sm" style={{ flex: 1, justifyContent: "center" }} icon={Bookmark} onClick={() => onToggleSave(ev.primary_id)}>
                {saved ? "Saved" : "Save"}
              </Btn>
              <WhatsAppButton ev={ev} />
            </div>
          </Card>
        </div>
      </div>

      {/* Already applied banner */}
      {isApplied && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, padding: "12px 16px", background: T.success + "12", borderRadius: 10, border: `1px solid ${T.success}33` }}>
          <CheckCircle size={16} color={T.success} />
          <span style={{ fontSize: 14.5, color: T.success, fontWeight: 600 }}>You have already registered for this event.</span>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 20, alignItems: "start" }}>
        <div>
          {/* Tabs */}
          <Card style={{ padding: 0, marginBottom: 18, overflow: "hidden" }}>
            <div style={{ display: "flex", overflowX: "auto", padding: "0 8px" }}>
              {tabs.map(t => (
                <button key={t} onClick={() => setActiveTab(t)} style={{
                  padding: "14px 22px", border: "none", background: "none", cursor: "pointer",
                  fontWeight: activeTab === t ? 800 : 600, color: activeTab === t ? T.orange : T.muted,
                  borderBottom: `2px solid ${activeTab === t ? T.orange : "transparent"}`, fontSize: 15,
                  textTransform: "capitalize", whiteSpace: "nowrap", fontFamily: "inherit",
                }}>{t}</button>
              ))}
            </div>
          </Card>

          {/* Timeline */}
          <Card style={{ padding: 20, marginBottom: 18, overflow: "visible" }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: T.navy, textTransform: "uppercase", letterSpacing: .5, marginBottom: 14 }}>Event Timeline</div>
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              {timeline.map((t, i) => (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
                  {i < timeline.length - 1 && (
                    <div style={{ position: "absolute", top: 12, left: "50%", width: "100%", height: 2, background: t.status === "completed" ? T.success : T.border, zIndex: 0 }} />
                  )}
                  <div style={{ width: 24, height: 24, borderRadius: "50%", background: t.color, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1, border: "2px solid #fff", flexShrink: 0 }}>
                    <t.icon size={10} color="#fff" />
                  </div>
                  <div style={{ marginTop: 6, textAlign: "center", fontSize: 13, fontWeight: 700, color: t.status === "upcoming" ? T.muted : T.text, lineHeight: 1.3 }}>{t.label}</div>
                  <div style={{ fontSize: 12.5, marginTop: 1, textAlign: "center", color: T.muted }}>{t.date}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Campus / event photos — builds emotional trust */}
          <PhotoStrip photos={ev.campus_photos} title="Campus & Event Photos" />

          {/* Tab content */}
          {activeTab === "overview" && (
            <Card style={{ padding: 20 }}>
              <div style={{ fontWeight: 800, fontSize: 17, color: T.navy, marginBottom: 12 }}>Event Overview</div>
              <p style={{ color: T.text, fontSize: 15, lineHeight: 1.7, margin: "0 0 16px" }}>
                {ev.description || "This event brings together leading educational institutions and innovators to explore collaboration and innovation."}
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {[["Category", ev.event_category], ["Industry", ev.industry], ["Event Mode", ev.event_mode], ["Last Date to Register", fmtDate(ev.reg_close_date)]].filter(([,v]) => v).map(([l, v]) => (
                  <div key={l} style={{ padding: 11, background: T.bg, borderRadius: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, color: T.muted }}>{l}</div>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: T.text, marginTop: 3 }}>{v}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {activeTab === "agenda" && (
            <Card style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10 }}>
                <CalendarClock size={18} color={T.orange} />
                <div style={{ fontWeight: 800, fontSize: 17, color: T.navy }}>Event Agenda</div>
              </div>
              {agenda.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px", color: T.muted }}>
                  <CalendarClock size={32} color={T.border} style={{ marginBottom: 12 }} />
                  <div style={{ fontSize: 15, fontWeight: 600 }}>Agenda will be published closer to the event date.</div>
                </div>
              ) : (() => {
                // Group agenda items by date so multi-day university events read as "Day 1 / Day 2…"
                const groups = {};
                agenda.forEach(item => {
                  const key = item.date || "tbd";
                  (groups[key] = groups[key] || []).push(item);
                });
                const dateKeys = Object.keys(groups).sort((a, b) => {
                  if (a === "tbd") return 1;
                  if (b === "tbd") return -1;
                  return new Date(a) - new Date(b);
                });
                const multiDay = dateKeys.filter(k => k !== "tbd").length > 1;

                return dateKeys.map((dateKey, dIdx) => (
                  <div key={dateKey}>
                    {/* Day header — full-width banner so multi-day events read clearly */}
                    <div style={{
                      background: T.navy, color: "#fff", padding: "12px 22px",
                      display: "flex", alignItems: "center", gap: 12,
                      borderTop: dIdx > 0 ? `1px solid ${T.border}` : "none",
                    }}>
                      <span style={{
                        background: T.orange, color: "#fff", borderRadius: 6,
                        padding: "4px 12px", fontSize: 12.5, fontWeight: 800,
                        letterSpacing: .6, textTransform: "uppercase", whiteSpace: "nowrap",
                      }}>
                        {multiDay ? `Day ${dIdx + 1}` : "Schedule"}
                      </span>
                      {dateKey !== "tbd" && (
                        <span style={{ fontSize: 14, fontWeight: 700 }}>{fmtDate(dateKey)}</span>
                      )}
                    </div>

                    {/* Agenda items */}
                    <div>
                      {groups[dateKey].map((item, idx) => (
                        <div key={idx} style={{
                          display: "flex", gap: 20, padding: "18px 22px", flexWrap: "wrap",
                          borderBottom: `1px solid ${T.border}`,
                          background: idx % 2 === 0 ? "#fff" : T.bg + "90",
                        }}>
                          {/* Time */}
                          <div style={{
                            display: "inline-flex", alignItems: "center", gap: 7,
                            background: T.light, color: T.navy, borderRadius: 8,
                            padding: "8px 14px", fontSize: 14, fontWeight: 800,
                            whiteSpace: "nowrap", height: "fit-content", flexShrink: 0,
                          }}>
                            <Clock size={14} />
                            {fmtTime(item.startTime)}
                            {item.endTime ? ` – ${fmtTime(item.endTime)}` : ""}
                          </div>

                          {/* Title + description */}
                          <div style={{ flex: 1, minWidth: 220 }}>
                            <div style={{ fontSize: 16, fontWeight: 800, color: T.text, lineHeight: 1.4 }}>
                              {item.title}
                            </div>
                            {item.description && (
                              <div style={{ fontSize: 14, color: T.muted, marginTop: 5, lineHeight: 1.7 }}>
                                {item.description}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </Card>
          )}
          {activeTab === "venue" && (
            <Card style={{ padding: 20 }}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 20,
                  color: T.navy,
                  marginBottom: 14
                }}
              >
                Venue Information
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10
                }}
              >
                {[
                  ["City", ev.city],
                  ["Country", ev.country],
                  ["Venue", ev.venue]
                ]
                  .filter(([, v]) => v)
                  .map(([l, v]) => (
                    <div
                      key={l}
                      style={{
                        padding: 11,
                        background: T.bg,
                        borderRadius: 8,
                        gridColumn: l === "Venue" ? "span 2" : undefined
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12.5,
                          textTransform: "uppercase",
                          fontWeight: 700
                        }}
                      >
                        {l}
                      </div>

                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: T.text,
                          marginTop: 3
                        }}
                      >
                        {v}
                      </div>
                    </div>
                  ))}

                {ev.venue_link && (
                  <div
                    style={{
                      padding: 11,
                      background: "#EFF6FF",
                      borderRadius: 8,
                      gridColumn: "span 2"
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12.5,
                        textTransform: "uppercase",
                        fontWeight: 700
                      }}
                    >
                      Venue Directions
                    </div>

                    <a
                      href={ev.venue_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 6,
                        color: T.accent,
                        fontWeight: 700,
                        textDecoration: "none"
                      }}
                    >
                      <MapPin size={15} />
                      Open in Google Maps
                    </a>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div>
          {/* Key information */}
          <Card style={{ padding: 16, marginBottom: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: T.navy, marginBottom: 12, textTransform: "uppercase", letterSpacing: .5 }}>Key Information</div>
            {[
              ["Event Dates", fmtDateRange(ev.event_start_date, ev.event_end_date)],
              (ev.event_time || ev.reg_open_time) ? ["Event Time", fmtTime(ev.event_time || ev.reg_open_time)] : null,
              ["Last Date to Register", fmtDate(ev.reg_close_date)],
              ["Location", `${ev.city}, ${ev.country}`],
            ].filter(Boolean).map(([l, v]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 14.5, marginBottom: 9 }}>
                <span style={{ color: T.muted }}>{l}</span>
                <span style={{ fontWeight: 700, color: T.text, textAlign: "right" }}>{v}</span>
              </div>
            ))}
          </Card>

          {/* Organizer */}
          {/* <Card style={{ padding: 16 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: T.navy, marginBottom: 10, textTransform: "uppercase", letterSpacing: .5 }}>Organizer</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <LogoAvatar logo={ev.institution_logo} name={ev.school_name} size={40} bg={bannerColor} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 14.5, color: T.text }}>{ev.school_name || `${ev.city} Event Team`}</div>
                <div style={{ fontSize: 13 }}>Event Organizer</div>
              </div>
            </div>
            {[[Mail, "organizer@eduplatform.com"], [Phone, "+91-XXXXX-XXXXX"], [Globe, "www.eduplatform.com"]].map(([Icon, val]) => (
              <div key={val} style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 13.5, marginBottom: 5 }}>
                <Icon size={11} /> {val}
              </div>
            ))}
          </Card> */}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SCREEN 3 — APPLICATION SUBMISSION
// ══════════════════════════════════════════════════════════════════════════════
function ApplicationSubmission({ ev, onBack, onBackToListing, onGoToMyApps, onSubmitSuccess }) {
  const uc = getUserCredentials();
  const [numReps, setNumReps]   = useState(2);
  const [notes, setNotes]       = useState("");
  const [agreed, setAgreed]     = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState("");

  const handleSubmit = async () => {
    if (!agreed) { setError("Please confirm the declaration before submitting."); return; }
    setSubmitting(true);
    setError("");
    try {
      const creds = CreateData();
      const now   = getFormattedDate();
      await axios.post(creds[1], {
        database: "edu_platform",
        table: "event_applications",
        formData: {
          event_id:            ev.primary_id,
          applicant_user_id:   uc.user_id,
          applicant_entity_id: uc.selectedentity || uc.entities_id,
          tenant_id:           uc.tenants_id,
          group_id:            uc.groups_id,
          num_representatives: numReps,
          additional_notes:    notes || null,
          application_status:  "applied",
          applied_on:          now,
          created_by:          uc.user_id,
          created_date:        now,
          modified_date:       now,
          is_active:           1,
        },
      }, creds[0]);
      setSubmitted(true);
      onSubmitSuccess(ev.primary_id); // notify parent to add to applied set
    }  catch (err) {

  const errorStr = err?.response?.data?.error || "";
  console.error("Submit error:", err.response);
  if (errorStr.includes("1062") || errorStr.toLowerCase().includes("duplicate entry")) {
    setAlreadyApplied(true);
    onSubmitSuccess(ev.primary_id); // sync parent so listing card updates too
  } else {
    setError(
      err?.response?.data?.Message || "Submission failed. Please try again."
    );
  }
} finally {
  setSubmitting(false);
}
  };

  const bannerColor = getBannerColor(ev.primary_id);

  if (submitted) {
    return (
      <div style={{ fontFamily: FONT }}>
        <Breadcrumb items={[
          { label: "Event Listings", onClick: onBackToListing },
          { label: ev.event_name, onClick: onBack },
          { label: "Application Submitted" },
        ]} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 360 }}>
          <Card style={{ padding: 48, textAlign: "center", maxWidth: 440 }}>
            <div style={{ width: 64, height: 64, background: T.success + "20", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
              <CheckCircle size={32} color={T.success} />
            </div>
            <h3 style={{ color: T.navy, margin: "0 0 8px", fontSize: 20 }}>Application Submitted!</h3>
            <p style={{ color: T.muted, fontSize: 15, lineHeight: 1.6 }}>
              Your application for <strong>{ev.event_name}</strong> has been submitted successfully. You'll be notified about the review status.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 22 }}>
              <Btn variant="outline" icon={Search} onClick={onBackToListing}>Browse Events</Btn>
              <Btn variant="primary" icon={ClipboardList} onClick={onGoToMyApps}>My Applications</Btn>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: FONT }}>
      <Breadcrumb items={[
        { label: "Event Listings", onClick: onBackToListing },
        { label: ev.event_name, onClick: onBack },
        { label: "Submit Application" },
      ]} />

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
        <button onClick={onBack}
          style={{ background: T.light, border: "none", borderRadius: 8, padding: "8px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, color: T.navy, fontWeight: 700, fontSize: 15, fontFamily: "inherit" }}>
          <ArrowLeft size={14} />
        </button>
        <div>
          <h3 style={{ margin: 0, fontSize: 19, fontWeight: 900, color: T.navy }}>Submit Application</h3>
          <p style={{ margin: "3px 0 0", fontSize: 17 }}>{ev.event_name} · Entity #{uc.selectedentity || uc.entities_id}</p>
        </div>
      </div>

      {/* Progress steps */}
      <div style={{ display: "flex", gap: 8, marginBottom: 22 }}>
        {["Review Profile", "Add Details", "Submit"].map((s, i) => (
          <div key={i} style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 24, height: 24, borderRadius: "50%", background: i < 2 ? T.success : T.orange, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
              {i < 2 ? <Check size={11} /> : 3}
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: i < 2 ? T.success : T.navy }}>{s}</div>
            {i < 2 && <div style={{ flex: 1, height: 2, background: i < 1 ? T.success : T.border }} />}
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 280px", gap: 20, alignItems: "start" }}>
        <div>
          {/* Profile card */}
          <Card style={{ marginBottom: 16 }}>
            <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.border}`, background: `linear-gradient(135deg, ${T.navy}, ${T.navyMid})`, display: "flex", alignItems: "center", gap: 10 }}>
              <Building size={13} color={T.orange} />
              <span style={{ fontWeight: 800, fontSize: 15, color: "#fff" }}>School Profile (Auto-filled)</span>
              <div style={{ marginLeft: "auto" }}><StatusBadge status="applied" label="Profile Active" /></div>
            </div>
            <div style={{ padding: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[["School Name","Your Institution"],["City","City, Country"],["School Type","Private / Public"],["Representative","Primary Contact"],["Email","contact@school.edu"],["Contact","+XX-XXXXXXXXXX"]].map(([l, v]) => (
                <div key={l}>
                  <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>{l}</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginTop: 2 }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ padding: "0 18px 14px", display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["Brochure.pdf","Accreditation.pdf","Logo.png"].map(f => (
                <div key={f} style={{ display: "flex", alignItems: "center", gap: 5, background: T.light, borderRadius: 6, padding: "4px 9px", fontSize: 13, color: T.text }}>
                  <Paperclip size={10} /> {f}
                </div>
              ))}
            </div>
          </Card>

          {/* Details */}
          <Card style={{ padding: 20, marginBottom: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: T.navy, marginBottom: 14 }}>Application Details</div>
            <Field label="Number of Representatives Attending" required hint="Max 3 representatives per institution">
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <button onClick={() => setNumReps(p => Math.max(1, p - 1))}
                  style={{ width: 34, height: 34, borderRadius: 8, border: `1.5px solid ${T.border}`, background: T.light, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Minus size={13} />
                </button>
                <span style={{ fontSize: 38, fontWeight: 900, color: T.navy, minWidth: 28, textAlign: "center" }}>{numReps}</span>
                <button onClick={() => setNumReps(p => Math.min(3, p + 1))}
                  style={{ width: 34, height: 34, borderRadius: 8, border: `1.5px solid ${T.border}`, background: T.light, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Plus size={13} />
                </button>
              </div>
            </Field>
            <Field label="Additional Notes" hint="Optional: Any specific requirements or information for the organizer">
              <TextareaInput value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="e.g. We are particularly interested in the AI workshop sessions…" style={{ minHeight: 90 }} />
            </Field>
          </Card>

          {/* Declaration */}
          <Card style={{ padding: 16, border: `1.5px solid ${agreed ? T.success : T.border}`, transition: "border .2s" }}>
            <label style={{ display: "flex", gap: 13, cursor: "pointer", alignItems: "flex-start" }}>
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: 3 }} />
              <span style={{ fontSize: 15, color: T.text, lineHeight: 1.6 }}>
                I confirm that all information provided in my school profile is accurate and up-to-date. I agree to the event's participation terms and understand that misrepresentation may result in disqualification.
              </span>
            </label>
          </Card>

          {alreadyApplied && (
  <div style={{
    display: "flex", alignItems: "flex-start", gap: 10, marginTop: 16,
    padding: "14px 16px",
    background: "rgba(124,58,237,.07)",
    border: "1.5px solid rgba(124,58,237,.3)",
    borderRadius: 10,
  }}>
    <CheckCircle size={18} color={T.purple} style={{ flexShrink: 0, marginTop: 1 }} />
    <div>
      <div style={{ fontWeight: 800, fontSize: 15, color: T.purple }}>
        Application Already Exists
      </div>
      <div style={{ fontSize: 17, marginTop: 3, lineHeight: 1.6 }}>
        Your institution has already applied to <strong>{ev.event_name}</strong>.
        Head to <strong>My Applications</strong> to track its status.
      </div>
      <div style={{ marginTop: 10 }}>
        <Btn variant="primary" size="sm" icon={ClipboardList} onClick={onGoToMyApps}>
          View My Applications
        </Btn>
      </div>
    </div>
  </div>
)}

          {error && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, padding: "10px 14px", background: "rgba(220,38,38,.07)", borderRadius: 8, border: `1px solid rgba(220,38,38,.25)` }}>
              <AlertCircle size={14} color={T.danger} />
              <span style={{ fontSize: 15, color: T.danger }}>{error}</span>
              <button onClick={() => setError("")} style={{ marginLeft: "auto", background: "none", border: "none", color: T.danger, cursor: "pointer" }}><X size={13} /></button>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
            <Btn variant="outline" icon={Save}>Save Draft</Btn>
            <Btn variant="primary" icon={Send} onClick={handleSubmit}
              disabled={submitting || !agreed || alreadyApplied}>
              {submitting ? "Submitting…" : "Submit Application"}
            </Btn>
          </div>
        </div>

        {/* Event sidebar */}
        <Card style={{ padding: 16 }}>
          <div style={{ height: 68, background: `linear-gradient(135deg, ${bannerColor}, ${T.navy})`, borderRadius: 8, marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 10px" }}>
            <span style={{ color: "#fff", fontWeight: 900, fontSize: 17, textAlign: "center" }}>{ev.event_name}</span>
          </div>
          {[["Location",`${ev.city}, ${ev.country}`],["Event Dates",fmtDateRange(ev.event_start_date,ev.event_end_date)],["Reg. Closes",fmtDate(ev.reg_close_date)],["Slots",ev.max_invitations||"Open"]].map(([l,v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 17, marginBottom: 7 }}>
              <span style={{ color: T.muted }}>{l}</span>
              <span style={{ fontWeight: 700, color: T.text }}>{v}</span>
            </div>
          ))}
          <div style={{ marginTop: 10, padding: 10, background: T.bg, borderRadius: 8, fontSize: 13, textAlign: "center" }}>
            <AlertCircle size={10} style={{ marginRight: 4 }} />
            Submitting as Entity #{uc.selectedentity || uc.entities_id}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SCREEN 4 — MY APPLICATIONS
// ══════════════════════════════════════════════════════════════════════════════
function MyApplications({ applications, loading, onRefresh, onViewEvent, uc }) {
  const [search, setSearch]           = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy]           = useState("applied_on");
  const [page, setPage]               = useState(1);
  const PAGE_SIZE = 10;

const filtered = applications.filter(a => {
  const q = search.toLowerCase().trim();

  if (!q) return true;

  return [
    a.event_name,
    a.school_name,
    a.country,
    a.state,
    a.city,
    a.application_status,
    a.review_status,
    a.invitation_status,
    fmtDate(a.applied_on),
    fmtDate(a.reviewed_on),
    fmtDate(a.responded_on),
    fmtDate(a.event_start_date),
    fmtDate(a.event_end_date),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(q);
});

  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const statusCounts = applications.reduce((acc, a) => { acc[a.application_status] = (acc[a.application_status] || 0) + 1; return acc; }, {});

  const handleExport = () => {
    const headers = [
      "Event Name",
      "Location",
      "Applied On",
      "Application Status",
      "Review Status",
      "Invitation Status",
      "Last Updated",
    ];

    const rows = filtered.map((a) => ([
      a.event_name || "",
      [a.city, a.country].filter(Boolean).join(", "),
      fmtDate(a.applied_on),
      a.application_status || "",
      a.review_status || "",
      a.invitation_status || "",
      fmtDate(a.last_updated_on),
    ]));

    downloadCSV(
      `my-applications-${new Date().toISOString().slice(0, 10)}.csv`,
      headers,
      rows
    );
  };

  return (
    <div style={{ fontFamily: FONT }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="outline" size="sm" icon={RefreshCw} onClick={onRefresh}>Refresh</Btn>
          <Btn variant="outline" size="sm" icon={Download} onClick={handleExport}>Export</Btn>
        </div>
      </div>

      {/* Status pill filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        <button onClick={() => { setStatusFilter(""); setPage(1); }}
          style={{ background: !statusFilter ? T.navy : T.card, color: !statusFilter ? "#fff" : T.text, border: `1px solid ${T.border}`, borderRadius: 99, padding: "5px 14px", fontSize: 17, fontWeight: 700, cursor: "pointer", transition: "all .15s" }}>
          All ({applications.length})
        </button>
        {Object.entries(statusCounts).map(([s, cnt]) => {
          const m = STATUS_META[s] || { bg: "#f3f4f6", color: "#6b7280" };
          const isActive = statusFilter === s;
          return (
            <button key={s} onClick={() => { setStatusFilter(isActive ? "" : s); setPage(1); }}
              style={{ background: isActive ? m.color : m.bg, color: isActive ? "#fff" : m.color, border: `1px solid ${m.color}44`, borderRadius: 99, padding: "5px 14px", fontSize: 17, fontWeight: 700, cursor: "pointer", transition: "all .15s" }}>
              {s.replace(/_/g, " ")} ({cnt})
            </button>
          );
        })}
      </div>

      {/* Search + sort bar */}
      <Card style={{ padding: "11px 16px", marginBottom: 18, overflow: "visible" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
            <Search size={13} color={T.muted} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by event name…" style={{ ...inputBase, paddingLeft: 32, fontSize: 15 }} />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ ...inputBase, width: 180, fontSize: 17 }}>
            <option value="">All Statuses</option>
            {["applied","pending_review","shortlisted","invited","accepted","rejected","declined","expired"].map(s => (
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
            ))}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...inputBase, width: 170, fontSize: 17 }}>
            <option value="applied_on">Sort: Applied Date</option>
            <option value="modified_date">Sort: Last Updated</option>
            <option value="event_name">Sort: Event Name</option>
          </select>
        </div>
      </Card>

      {/* Table */}
      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 240, gap: 12 }}>
          <div style={{ width: 32, height: 32, border: `4px solid ${T.light}`, borderTop: `4px solid ${T.orange}`, borderRadius: "50%", animation: "spin .8s linear infinite" }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <span style={{ color: T.muted, fontSize: 15 }}>Loading applications…</span>
        </div>
      ) : (
        <Card style={{ overflow: "visible" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${T.navy}, ${T.navyMid})` }}>
                  {["Event Name","School", "Location", "Event Dates", "Applied On", "App Status", "Review Status", "Invitation", "Last Updated", ""].map(h => (
                    <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "rgb(255, 255, 255)", textTransform: "uppercase", letterSpacing: .5, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: 48, textAlign: "center" }}>
                      <FileText size={28} color={T.border} style={{ marginBottom: 8 }} />
                      <div style={{ fontWeight: 700, fontSize: 15 }}>No applications found</div>
                      <div style={{ fontSize: 17, marginTop: 4 }}>Try adjusting your search or filters</div>
                    </td>
                  </tr>
                ) : paginated.map((a, i) => (
                  <tr key={a.primary_id} style={{ borderTop: `1px solid ${T.border}`, background: i % 2 === 0 ? "#fff" : T.bg + "88", transition: "background .12s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(10,35,82,.03)"}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "#fff" : T.bg + "88"}>
                    <td style={{ padding: "12px 14px", fontSize: 17, fontWeight: 700, color: T.navy, maxWidth: 200 }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.event_name}</div>
                    </td>
                    <td style={{ padding: "12px 14px", fontSize: 17, whiteSpace: "nowrap" }}>{a.school_name}</td>
                    <td style={{ padding: "12px 14px", fontSize: 17, whiteSpace: "nowrap" }}>{a.city}, {a.country}</td>
                    <td style={{ padding: "12px 14px", fontSize: 17, whiteSpace: "nowrap" }}>{fmtDateRange(a.event_start_date, a.event_end_date)}</td>
                    <td style={{ padding: "12px 14px", fontSize: 17, whiteSpace: "nowrap" }}>{fmtDate(a.applied_on)}</td>
                    <td style={{ padding: "12px 14px" }}><StatusBadge status={a.application_status} /></td>
                    <td style={{ padding: "12px 14px" }}>{a.review_status ? <StatusBadge status={a.review_status} /> : <span style={{ color: T.border, fontSize: 17 }}>—</span>}</td>
                    <td style={{ padding: "12px 14px" }}>{a.invitation_status ? <StatusBadge status={a.invitation_status} /> : <span style={{ color: T.border, fontSize: 17 }}>—</span>}</td>
                    <td style={{ padding: "12px 14px", fontSize: 17, whiteSpace: "nowrap" }}>{fmtDate(a.modified_date)}</td>
                    <td style={{ padding: "12px 14px" }}>
                      <Btn variant="ghost" size="sm" icon={Eye} onClick={() => onViewEvent(a)}>View</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ padding: "12px 16px", borderTop: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <span style={{ fontSize: 17 }}>
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} applications
            </span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Btn variant="outline" size="sm" icon={ChevronLeft} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Prev</Btn>
              {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1).map((p, idx, arr) => (
                <React.Fragment key={p}>
                  {idx > 0 && arr[idx - 1] !== p - 1 && <span style={{ color: T.muted, fontSize: 17 }}>…</span>}
                  <button onClick={() => setPage(p)}
                    style={{ background: page === p ? T.navy : T.light, color: page === p ? "#fff" : T.navy, border: "none", borderRadius: 6, padding: "5px 10px", fontSize: 17, fontWeight: 700, cursor: "pointer", minWidth: 30 }}>
                    {p}
                  </button>
                </React.Fragment>
              ))}
              <Btn variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                Next <ChevronRight size={13} />
              </Btn>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT: EventListings
// ══════════════════════════════════════════════════════════════════════════════
export function EventListings() {
  const uc = getUserCredentials();

  // "listing" | "details" | "apply" | "myapps"
  const [screen, setScreen]           = useState("listing");
  const [events, setEvents]           = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [savedIds, setSavedIds]       = useState({});

  // Event currently shown in the one-click "Register Now" modal (null = closed)
  const [quickRegisterEvent, setQuickRegisterEvent] = useState(null);

  // Applied events — Set of event primary_ids the entity has applied to
  const [appliedEventIds, setAppliedEventIds] = useState(new Set());

  // My Applications data
  const [applications, setApplications]   = useState([]);
  const [appsLoading, setAppsLoading]     = useState(false);
  // Add state
const [invitations, setInvitations]     = useState([]);
const [invLoading, setInvLoading]       = useState(false);

// Add fetch function
const fetchInvitations = async () => {
  setInvLoading(true);
  try {
    const creds = Fetchexecutequery();
    const res = await axios.post(creds[1], {
      query: `SELECT ei.primary_id, ei.event_id, ei.application_id,
                     ei.invitation_status, ei.invited_on,
                     ei.acceptance_deadline, ei.responded_on,sp.school_name,e.state,
                     e.event_name, e.event_category, e.city, e.country,
                     e.event_start_date, e.event_end_date
              FROM edu_platform.event_invitations ei
              JOIN edu_platform.events e ON e.primary_id = ei.event_id
              LEFT JOIN edu_platform.school_profile sp ON e.user_id = sp.user_id
              WHERE ei.applicant_entity_id = ${uc.selectedentity || uc.entities_id}
                AND ei.tenant_id = ${uc.tenants_id}
                AND ei.is_active = 1
              ORDER BY ei.invited_on DESC;`,
    }, creds[0]);
    setInvitations(res.data || []);
  } catch (err) { console.error("Fetch invitations error:", err); }
  finally { setInvLoading(false); }
};

// Add to useEffect
useEffect(() => {
  fetchEvents();
  fetchApplications();
  fetchInvitations(); // ← add
}, []);

  // Update activeTab to include invitations
  const activeTab = ["listing","details","apply"].includes(screen)
    ? "listing"
    : screen === "invitations"
    ? "invitations"
    : "myapps";

  // Update handleTabChange
  const handleTabChange = (tab) => {
    if (tab === "listing")     goToListing();
    else if (tab === "myapps") goToMyApps();
    else { fetchInvitations(); setScreen("invitations"); }
  };

  // ── Fetch published events only ───────────────────────────────────────────
  const fetchEvents = async () => {
  setEventsLoading(true);

  try {
    const creds = Fetchexecutequery();

    const res = await axios.post(
      creds[1],
      {
        query: `
          SELECT
              e.primary_id,
              e.user_id,
              e.event_name,
              e.event_category,
              e.event_mode,
              e.industry,
              e.country,
              e.state,
              e.city,
              e.venue,
              e.event_start_date,
              e.event_end_date,
              e.event_status,
              e.reg_open_date,
              e.reg_close_date,
              e.reg_open_time,
              e.reg_close_time,
              e.review_start_date,
              e.review_end_date,
              e.invitation_release_date,
              e.acceptance_deadline,
              e.max_applicants,
              e.max_invitations,
              e.current_applications,
              e.current_invitations,
              e.eligibility_criteria,
              e.description,
              e.banner_file_name,
              e.brochure_file_name,
              e.key_highlights,
              e.event_agenda,e.venue_link,

              COALESCE(sp.school_name, cp.college_name, up.university_name) AS school_name,
              COALESCE(sp.school_logo, cp.institution_logo, up.university_logo) AS institution_logo,
              sp.campus_photos AS campus_photos

          FROM edu_platform.events e
          LEFT JOIN edu_platform.school_profile sp
                 ON e.user_id = sp.primary_id
          LEFT JOIN edu_platform.college_profile cp
                 ON e.user_id = cp.primary_id
          LEFT JOIN edu_platform.university_profile up
                 ON e.user_id = up.primary_id

          WHERE e.is_active = 1
            AND e.tenant_id = ${uc.tenants_id}
            AND e.event_status = 'published'

          ORDER BY e.primary_id DESC
        `,
      },
      creds[0]
    );

    setEvents(res.data || []);
  } catch (err) {
    console.error("Fetch events error:", err);
  } finally {
    setEventsLoading(false);
  }
};

  // ── Fetch this entity's applications ─────────────────────────────────────
  const fetchApplications = async () => {
    setAppsLoading(true);
    try {
      const creds = Fetchexecutequery();
      const res = await axios.post(creds[1], {
        query: `SELECT ea.primary_id, ea.event_id, ea.application_status,
                       ea.num_representatives, ea.additional_notes,
                       ea.applied_on, ea.reviewed_on, ea.invited_on,
                       ea.response_deadline, ea.responded_on,
                       ea.review_status, ea.invitation_status,
                       ea.modified_date,sp.school_name,e.state,
                       e.event_name, e.event_category, e.city, e.country,
                       e.event_start_date, e.event_end_date, e.event_status AS ev_status
                FROM edu_platform.event_applications ea
                JOIN edu_platform.events e ON e.primary_id = ea.event_id
                LEFT JOIN edu_platform.school_profile sp
                  ON e.user_id = sp.user_id
                WHERE ea.applicant_entity_id = ${uc.selectedentity || uc.entities_id}
                  AND ea.tenant_id = ${uc.tenants_id}
                  AND ea.is_active = 1 and ea.applicant_user_id = ${uc.user_id}
                ORDER BY ea.applied_on DESC;`,
      }, creds[0]);
      const data = res.data || [];
      setApplications(data);
      // Rebuild the applied set
      setAppliedEventIds(new Set(data.map(a => a.event_id)));
    } catch (err) { console.error("Fetch applications error:", err); }
    finally { setAppsLoading(false); }
  };



  // ── Navigation handlers ───────────────────────────────────────────────────
  const goToListing  = () => setScreen("listing");
  const goToMyApps   = () => { fetchApplications(); setScreen("myapps"); };
  const goToDetails  = (ev) => { setSelectedEvent(ev); setScreen("details"); };
  // "Apply" is blocked at the card level if already applied; this is the deep guard
  const goToApply    = (ev) => {
    if (appliedEventIds.has(ev.primary_id)) {
      // Already applied — go to details and show the banner instead
      setSelectedEvent(ev);
      setScreen("details");
      return;
    }
    setSelectedEvent(ev);
    setScreen("apply");
  };

  // Called after successful submission to immediately reflect applied status
  const handleSubmitSuccess = (eventId) => {
    setAppliedEventIds(prev => new Set([...prev, eventId]));
    fetchApplications(); // refresh list in background
  };

  const handleToggleSave = (id) => setSavedIds(p => ({ ...p, [id]: !p[id] }));
  



  // ── Stats (listing screen only) ───────────────────────────────────────────
  const totalEvents = events.length;
  const openEvents  = events.filter(e => e.event_status === "registration_open").length;
  const savedCount  = Object.values(savedIds).filter(Boolean).length;
  const categories  = new Set(events.map(e => e.event_category)).size;
  const pendingInvCount = invitations.filter(i => i.invitation_status === "pending").length;


  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: FONT }}>
      <div style={{ margin: "0 auto", padding: "20px 32px 48px", boxSizing: "border-box" }}>

        {/* Tab bar — hidden on the Details screen; "Back to Event Listings" replaces it there */}
        {screen !== "details" && (
          <TopTabBar
            active={activeTab}
            onChange={handleTabChange}
            appCount={applications.length}
            invCount={pendingInvCount}
          />
        )}

        {/* Stat cards — listing screen only */}
        {screen === "listing" && (
          <div style={{ display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap" }}>
            {[
              { label: "Total Events",      value: totalEvents, icon: Calendar,     color: T.accent  },
              { label: "Registration Open", value: openEvents,  icon: Zap,          color: T.success },
              { label: "Categories",        value: categories,  icon: Filter,       color: T.purple  },
              { label: "My Applications",   value: applications.length, icon: ClipboardList, color: T.orange },
            ].map(sc => (
              <Card key={sc.label} style={{ padding: "16px 20px", flex: 1, minWidth: 140,height:"80px", cursor: sc.label === "My Applications" ? "pointer" : "default" }}
                onClick={sc.label === "My Applications" ? goToMyApps : undefined}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ color: T.muted, fontSize: 17, fontWeight: 700, letterSpacing: .8, textTransform: "uppercase" }}>{sc.label}</div>
                    <div style={{ fontSize: 30, fontWeight: 900, color: T.text, lineHeight: 1.1, marginTop: 4 }}>{sc.value}</div>
                  </div>
                  <div style={{ background: sc.color + "18", borderRadius: 12, padding: 10 }}>
                    <sc.icon size={20} color={sc.color} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Screen router */}
        {screen === "listing" && (
          <EventListing
            events={events}
            loading={eventsLoading}
            onViewDetails={goToDetails}
            onApply={goToApply}
            onQuickRegister={setQuickRegisterEvent}
            savedIds={savedIds}
            onToggleSave={handleToggleSave}
            appliedEventIds={appliedEventIds}
          />
        )}

        {screen === "details" && selectedEvent && (
          <EventDetails
            ev={selectedEvent}
            onBack={goToListing}
            onApply={goToApply}
            onQuickRegister={setQuickRegisterEvent}
            saved={!!savedIds[selectedEvent.primary_id]}
            onToggleSave={handleToggleSave}
            isApplied={appliedEventIds.has(selectedEvent.primary_id)}
          />
        )}

        {screen === "apply" && selectedEvent && (
          <ApplicationSubmission
            ev={selectedEvent}
            onBack={() => setScreen("details")}       // back arrow → event details
            onBackToListing={goToListing}              // breadcrumb "Event Listings" → listing
            onGoToMyApps={goToMyApps}                  // success screen "My Applications"
            onSubmitSuccess={handleSubmitSuccess}
          />
        )}

        {screen === "invitations" && (
          <MyInvitations
            invitations={invitations}
            loading={invLoading}
            onRefresh={fetchInvitations}
            onRespondSuccess={() => { fetchInvitations(); fetchApplications(); }}
            uc={uc}
          />
        )}

        {screen === "myapps" && (
          <MyApplications
            applications={applications}
            loading={appsLoading}
            onRefresh={fetchApplications}
            onViewEvent={(a) => {
              // Find the matching event in events list and go to details
              const ev = events.find(e => e.primary_id === a.event_id);
              if (ev) goToDetails(ev);
            }}
            uc={uc}
          />
        )}
      </div>

      {/* Floating "Register Now" CTA — sticky on the Event Details screen for mobile conversion */}
      {/* {screen === "details" && selectedEvent && !appliedEventIds.has(selectedEvent.primary_id) && (
        <FloatingRegisterButton onClick={() => setQuickRegisterEvent(selectedEvent)} />
      )} */}

      {/* One-click registration modal */}
      {quickRegisterEvent && (
        <QuickRegisterModal
          ev={quickRegisterEvent}
          onClose={() => setQuickRegisterEvent(null)}
          onSubmitSuccess={(eventId) => { handleSubmitSuccess(eventId); }}
          onGoToMyApps={() => { setQuickRegisterEvent(null); goToMyApps(); }}
        />
      )}
    </div>
  );
}

export default EventListings;